// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Alert, Button, Group, Modal, Select, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMedplum } from '@medplum/react';
import { type JSX, useState } from 'react';
import { botIdentifier } from '../../lib/bot-identifier';
import { showErrorNotification } from '../../utils/notifications';

/**
 * Selectable account types. Must stay in sync with the bot's
 * `CHANGE_ROLE_TARGETS` in
 * apps/provider-app/bots/services/change-provider-role/change-provider-role.ts.
 *
 * `admin` is mutually exclusive with the clinical roles — selecting it grants
 * Medplum project-admin (which bypasses AccessPolicy); selecting any clinical
 * role removes it.
 */
const ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'physician', label: 'Physician (MD)' },
  { value: 'nurse-practitioner', label: 'Nurse Practitioner (NP)' },
  { value: 'care-coordinator', label: 'Care Coordinator (RN)' },
  { value: 'clinical-ops-rcm', label: 'Clinical Ops / RCM' },
  { value: 'software-engineer', label: 'Software Engineer' },
  { value: 'admin', label: 'Administrator' },
];

interface ChangeRoleModalProps {
  opened: boolean;
  onClose: () => void;
  practitioner: {
    id: string;
    name: string;
    currentRole: string | undefined;
  };
  onSuccess: () => void;
}

/**
 * Admin-only modal for re-assigning a staff member's account type.
 *
 * Invokes the `change-provider-role` bot, which rewrites the PractitionerRole
 * coding and the ProjectMembership access policy (or the project-admin flag) to
 * match the selected account type. The bot independently verifies the requester
 * is a project admin, so this modal is the convenience layer, not the gate.
 */
export function ChangeRoleModal({ opened, onClose, practitioner, onSuccess }: ChangeRoleModalProps): JSX.Element {
  const medplum = useMedplum();
  const [role, setRole] = useState<string | null>(practitioner.currentRole ?? null);
  const [loading, setLoading] = useState(false);

  const unchanged = role === (practitioner.currentRole ?? null);

  const handleConfirm = async (): Promise<void> => {
    if (!role) {
      return;
    }
    setLoading(true);
    try {
      await medplum.executeBot(botIdentifier('change-provider-role'), {
        practitionerReference: `Practitioner/${practitioner.id}`,
        role,
      });
      const label = ROLE_OPTIONS.find((o) => o.value === role)?.label ?? role;
      notifications.show({
        color: 'green',
        title: 'Role updated',
        message: `${practitioner.name} is now ${label}.`,
      });
      onSuccess();
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Change role" size="md" data-testid="change-role-modal">
      <Stack gap="md">
        <Text size="sm">
          Change the account type for{' '}
          <Text component="span" fw={600}>
            {practitioner.name}
          </Text>
          .
        </Text>
        <Select
          label="Account type"
          data={ROLE_OPTIONS}
          value={role}
          onChange={setRole}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
          data-testid="change-role-select"
        />
        {role === 'admin' && (
          <Alert color="yellow" variant="light" data-testid="change-role-admin-warning">
            Administrators have full access to all data and settings, bypassing role-based permissions.
          </Alert>
        )}
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={loading} data-testid="change-role-cancel-button">
            Cancel
          </Button>
          <Button
            loading={loading}
            disabled={!role || unchanged}
            onClick={() => {
              handleConfirm().catch(console.error);
            }}
            data-testid="change-role-confirm-button"
          >
            Save role
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
